/**
 * Utility to write compiled PDFs directly into a folder on the user's PC
 * via the File System Access API (Chromium browsers). Falls back to ZIP
 * download for browsers that don't support it (Firefox/Safari).
 */
import JSZip from "jszip";

export function supportsFolderWrite() {
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

/**
 * Runs a set of preflight checks that explain WHY showDirectoryPicker may
 * fail on a specific PC even when the browser advertises support.
 * Returns { ok, reasons: string[], details: object }.
 */
export function diagnoseFolderWrite() {
  const reasons = [];
  const details = {};
  if (typeof window === "undefined") {
    reasons.push("Contesto non-browser (SSR).");
    return { ok: false, reasons, details };
  }
  details.userAgent = navigator.userAgent;
  details.protocol = window.location.protocol;
  details.host = window.location.host;
  details.isSecureContext = !!window.isSecureContext;
  details.inIframe = window !== window.top;
  details.hasApi = typeof window.showDirectoryPicker === "function";
  details.storageAccess = typeof document !== "undefined" && "requestStorageAccess" in document;

  if (!details.hasApi) {
    reasons.push(
      "API 'showDirectoryPicker' non disponibile in questo browser. Serve Chrome/Edge 86+ desktop (non supportato su Firefox, Safari, iPad/iPhone, e in alcune versioni enterprise di Edge)."
    );
  }
  if (!details.isSecureContext) {
    reasons.push(
      `La pagina non è in un contesto sicuro (protocol=${details.protocol}). La File System Access API funziona solo su HTTPS o localhost.`
    );
  }
  if (details.inIframe) {
    reasons.push(
      "La pagina è caricata dentro un iframe. Il browser blocca showDirectoryPicker dagli iframe senza il permesso 'cross-origin-isolated' o allow='directory-picker'."
    );
  }
  // Enterprise policy hint: Edge in ambienti aziendali può disabilitare la File System Access.
  // Non c'è un modo diretto per rilevarlo, ma se hasApi è true e comunque fallisce con
  // SecurityError significa quasi sempre policy o feature flag.
  return { ok: reasons.length === 0, reasons, details };
}

/**
 * Human-readable explanation of a DOMException / Error thrown by
 * showDirectoryPicker or subsequent writes. Suggests concrete fixes.
 */
export function explainFolderWriteError(e) {
  if (!e) return "Errore sconosciuto.";
  const name = e.name || "";
  const msg = e.message || String(e);
  const base = `${name}: ${msg}`;
  const map = {
    AbortError: "Selezione cartella annullata dall'utente.",
    NotAllowedError:
      "Il browser ha bloccato l'apertura del selettore cartella. Cause tipiche: click non riconosciuto come 'user gesture', policy aziendale che disabilita la File System Access API, o pagina caricata dentro un iframe. Verifica se il tasto è stato cliccato direttamente e riprova; se l'errore persiste, il tuo Edge ha probabilmente la feature disabilitata via policy di gruppo (chiedi all'IT o vedi edge://policy).",
    SecurityError:
      "Il browser ha rifiutato la richiesta per motivi di sicurezza. Verifica di essere su HTTPS, non dentro un iframe e senza estensioni che intercettano il picker. Su Edge Enterprise, potrebbe essere disabilitato via policy (edge://policy).",
    InvalidStateError:
      "Stato non valido: succede tipicamente quando la pagina è in un iframe o quando il picker è aperto due volte. Ricarica la pagina e riprova.",
    TypeError:
      "Chiamata API non valida. Aggiorna Edge/Chrome all'ultima versione e riprova.",
    NotFoundError:
      "Cartella non trovata o non accessibile (potrebbe essere un disco esterno smontato o un percorso di rete).",
  };
  return map[name] ? `${map[name]} (${base})` : `Errore durante la selezione cartella. ${base}`;
}

/**
 * Prompts the user to pick a parent folder, creates a subfolder named
 * `<baseName>-output` and writes each file inside the given ZIP blob into it
 * (overwriting any existing file with the same name).
 *
 * Returns { count, folderName, cancelled }.
 * Throws an Error with a detailed .userMessage on failure.
 */
export async function writeZipToFolder(zipBlob, baseName) {
  const diag = diagnoseFolderWrite();
  if (!diag.ok) {
    const err = new Error("File System Access non disponibile.");
    err.userMessage = diag.reasons.join(" · ");
    err.diagnostics = diag.details;
    throw err;
  }

  let dirHandle;
  try {
    dirHandle = await window.showDirectoryPicker({ mode: "readwrite" });
  } catch (e) {
    if (e && e.name === "AbortError") return { cancelled: true, count: 0 };
    const err = new Error(e && e.message ? e.message : "Selezione cartella fallita.");
    err.name = e && e.name ? e.name : "Error";
    err.userMessage = explainFolderWriteError(e);
    err.diagnostics = { ...diag.details, original: `${e?.name || ""}: ${e?.message || ""}` };
    throw err;
  }

  const folderName = `${sanitize(baseName)}-output`;
  let outDir;
  try {
    outDir = await dirHandle.getDirectoryHandle(folderName, { create: true });
  } catch (e) {
    const err = new Error("Impossibile creare la sottocartella di output.");
    err.name = e?.name || "Error";
    err.userMessage = `Non riesco a creare la sottocartella "${folderName}". ${explainFolderWriteError(e)}`;
    err.diagnostics = { ...diag.details, original: `${e?.name || ""}: ${e?.message || ""}`, folderName };
    throw err;
  }

  const zip = await JSZip.loadAsync(zipBlob);
  const entries = Object.values(zip.files).filter((f) => !f.dir);

  let count = 0;
  for (const entry of entries) {
    const content = await entry.async("blob");
    const filename = entry.name.split("/").pop();
    try {
      const fileHandle = await outDir.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(content);
      await writable.close();
      count++;
    } catch (e) {
      const err = new Error(`Impossibile scrivere il file "${filename}".`);
      err.name = e?.name || "Error";
      err.userMessage = `Errore scrittura di ${filename}: ${explainFolderWriteError(e)}`;
      err.diagnostics = {
        ...diag.details,
        original: `${e?.name || ""}: ${e?.message || ""}`,
        filename,
        writtenCount: count,
      };
      throw err;
    }
  }
  return { count, folderName, cancelled: false };
}

/** Fallback: download the ZIP as a file. */
export function downloadZip(zipBlob, filename = "pdf_compilati.zip") {
  const url = window.URL.createObjectURL(zipBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function sanitize(name) {
  return (name || "template")
    .replace(/\.[a-zA-Z0-9]+$/, "") // strip extension
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .slice(0, 80) || "template";
}
